export const AI_TOOL_NAMES = {
    LOAD_ENTITY_HISTORY: 'loadEntityHistory',
    LOAD_ENTITY: 'loadEntity',
    LOAD_ENTITY_FILES: 'loadEntityFiles',
    LOAD_ENTITY_TIMELINE: 'loadEntityTimeline',
    LOAD_ENTITY_COMMUNICATION: 'loadEntityCommunication',
    MATCH_INCIDENT_TO_OBLIGATIONS: 'matchIncidentToObligations',
    CREATE_TASK: 'createTask',
    // A Clara action run for the person who asked (named by its action key)
    COMPLETE_TASK: 'completeTask',
    // The read beside it: the record that proves a task is done
    FIND_TASK_EVIDENCE: 'findTaskEvidence',
    // General chat tools
    SEARCH_ENTITIES: 'searchEntities',
    GET_SYSTEM_HELP: 'getSystemHelp',
    SUGGEST_NEXT_STEPS: 'suggestNextSteps',
    GET_NAVIGATION_LINKS: 'getNavigationLinks',
    VISUALISE_DATA: 'visualiseData',
    FIND_RELEVANT_TOOLS: 'findRelevantTools',
    ANALYZE_COMPLIANCE_SITUATION: 'analyzeComplianceSituation',
    GET_OBLIGATION_DETAILS: 'getObligationDetails',
    ANALYZE_IDR_COMPLIANCE: 'analyzeIdrCompliance',
    // Generic procedure power-tools (the curated MCP procedure surface, always available)
    SEARCH_PROCEDURES: 'searchProcedures',
    GET_PROCEDURE_SCHEMA: 'getProcedureSchema',
    CALL_PROCEDURE: 'callProcedure',
} as const;

export type AiToolName = (typeof AI_TOOL_NAMES)[keyof typeof AI_TOOL_NAMES];

// Tool categories for easier handling
export const AI_TOOL_CATEGORIES = {
    READ_ONLY: [
        AI_TOOL_NAMES.LOAD_ENTITY_HISTORY,
        AI_TOOL_NAMES.LOAD_ENTITY,
        AI_TOOL_NAMES.LOAD_ENTITY_FILES,
        AI_TOOL_NAMES.LOAD_ENTITY_TIMELINE,
        AI_TOOL_NAMES.LOAD_ENTITY_COMMUNICATION,
        AI_TOOL_NAMES.MATCH_INCIDENT_TO_OBLIGATIONS,
        AI_TOOL_NAMES.FIND_TASK_EVIDENCE,
        // General chat tools
        AI_TOOL_NAMES.SEARCH_ENTITIES,
        AI_TOOL_NAMES.GET_SYSTEM_HELP,
        AI_TOOL_NAMES.SUGGEST_NEXT_STEPS,
        AI_TOOL_NAMES.GET_NAVIGATION_LINKS,
        AI_TOOL_NAMES.VISUALISE_DATA,
        // Generic procedure power-tools. callProcedure executes reads directly; mutations never run
        // through it — they pause for user approval (confirmProcedureCall) — so it stays read-only here.
        AI_TOOL_NAMES.SEARCH_PROCEDURES,
        AI_TOOL_NAMES.GET_PROCEDURE_SCHEMA,
        AI_TOOL_NAMES.CALL_PROCEDURE,
    ],
    DATA_MODIFYING: [AI_TOOL_NAMES.CREATE_TASK, AI_TOOL_NAMES.COMPLETE_TASK],
} as const;

export type ReadOnlyTool = (typeof AI_TOOL_CATEGORIES.READ_ONLY)[number];
export type DataModifyingTool = (typeof AI_TOOL_CATEGORIES.DATA_MODIFYING)[number];

// Helper function to check if a tool modifies data
export function isDataModifyingTool(toolName: AiToolName): toolName is DataModifyingTool {
    return (AI_TOOL_CATEGORIES.DATA_MODIFYING as readonly string[]).includes(toolName);
}

// Tool result types for better type safety
export interface AiToolResults {
    [AI_TOOL_NAMES.CREATE_TASK]: {
        success: boolean;
        taskId?: string;
        error?: string;
        formattedMessage: string;
    };
    [AI_TOOL_NAMES.LOAD_ENTITY]: {
        success: boolean;
        entityType?: string;
        entityId?: string;
        data?: any;
        error?: string;
        formattedMessage: string;
    };
    [AI_TOOL_NAMES.LOAD_ENTITY_HISTORY]: {
        total: number;
        items: Array<any>;
        hasMore: boolean;
        error?: string;
        formattedMessage: string;
    };
    [AI_TOOL_NAMES.LOAD_ENTITY_FILES]: {
        total: number;
        items: Array<any>;
        hasMore: boolean;
        error?: string;
        formattedMessage: string;
    };
    [AI_TOOL_NAMES.LOAD_ENTITY_TIMELINE]: {
        total: number;
        items: Array<any>;
        hasMore: boolean;
        error?: string;
        formattedMessage: string;
    };
    [AI_TOOL_NAMES.LOAD_ENTITY_COMMUNICATION]: {
        total: number;
        items: Array<any>;
        hasMore: boolean;
        error?: string;
        formattedMessage: string;
    };
    [AI_TOOL_NAMES.MATCH_INCIDENT_TO_OBLIGATIONS]: {
        total: number;
        items: Array<any>;
        threshold?: number;
        incident?: any;
        error?: string;
        formattedMessage: string;
    };
}
